import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import styled from "styled-components";
import { FiMenu, FiX } from "react-icons/fi";
import { useTranslation } from "react-i18next";
import { useLanguage } from "../../contexts/LanguageContext";

// 스타일 컴포넌트
const HeaderContainer = styled.header<{ $isScrolled: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background: ${(props) =>
    props.$isScrolled ? "rgba(0, 0, 0, 0.8)" : "transparent"};
  transition: all 0.3s ease;
`;

const HeaderTop = styled.div`
  @media (max-width: 768px) {
    display: none;
  }
`;

const TopInner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 8px 20px;
  display: flex;
  justify-content: flex-end;
  gap: 20px;
  font-size: 13px;
  color: #fff;
`;

const LanguageSwitch = styled.div`
  display: flex;
  gap: 10px;

  button {
    background: none;
    border: none;
    color: #fff;
    cursor: pointer;
    padding: 2px 8px;
    border-radius: 3px;
    transition: all 0.3s;

    &.active {
      background: #217fc4;
      color: white;
    }

    &:hover:not(.active) {
      background: rgba(255, 255, 255, 0.2);
    }
  }
`;

const HeaderMain = styled.div``;

const MainInner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Logo = styled(Link)<{ $isScrolled?: boolean }>`
  font-size: 24px;
  font-weight: bold;
  color: ${(props) => (props.$isScrolled ? "#4da6ff" : "#217fc4")};
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 10px;
  transition: color 0.3s;

  img {
    height: 40px;
    filter: ${(props) =>
      props.$isScrolled ? "brightness(0) invert(1)" : "none"};
    transition: filter 0.3s;
  }

  span {
    @media (max-width: 480px) {
      display: none;
    }
  }
`;

const Nav = styled.nav<{ $isOpen: boolean }>`
  @media (max-width: 768px) {
    position: fixed;
    top: 80px;
    right: ${(props) => (props.$isOpen ? "0" : "-100%")};
    width: 100%;
    max-width: 300px;
    height: calc(100vh - 80px);
    background: white;
    box-shadow: -2px 0 4px rgba(0, 0, 0, 0.1);
    transition: right 0.3s ease;
    overflow-y: auto;
  }
`;

const NavList = styled.ul`
  display: flex;
  list-style: none;
  margin: 0;
  padding: 0;

  @media (max-width: 768px) {
    flex-direction: column;
    padding: 20px;
  }
`;

const NavItem = styled.li`
  position: relative;

  /* 데스크톱에서 hover 시 드롭다운 표시 */
  @media (min-width: 769px) {
    &:hover > div {
      opacity: 1;
      visibility: visible;
      transform: translateY(0);
    }
  }

  @media (max-width: 768px) {
    width: 100%;
    border-bottom: 1px solid #e9ecef;
  }
`;

const NavLink = styled(Link)<{ $isActive?: boolean; $isScrolled?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 30px 20px;
  color: ${(props) => {
    if (props.$isScrolled) return props.$isActive ? "#4da6ff" : "#fff";
    return props.$isActive ? "#217fc4" : "#fff";
  }};
  text-decoration: none;
  font-weight: 700;
  transition: color 0.3s;
  position: relative;

  &:hover {
    color: ${(props) => (props.$isScrolled ? "#4da6ff" : "#217fc4")};
  }

  &::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: ${(props) => (props.$isActive ? "100%" : "0")};
    height: 3px;
    background: ${(props) => (props.$isScrolled ? "#4da6ff" : "#217fc4")};
    transition: width 0.3s;
  }

  &:hover::after {
    width: 100%;
  }

  @media (max-width: 768px) {
    padding: 15px 0;
    color: ${(props) => (props.$isActive ? "#217fc4" : "#333")};

    &::after {
      display: none;
    }
  }
`;

const Dropdown = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: 100%;
  left: 0;
  min-width: 200px;
  background: white;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  border-radius: 4px;
  opacity: 0;
  visibility: hidden;
  transform: translateY(-10px);
  transition: all 0.3s;
  z-index: 1001;

  @media (max-width: 768px) {
    position: static;
    box-shadow: none;
    border-radius: 0;
    padding-left: 20px;
    opacity: 1;
    visibility: visible;
    transform: none;
    display: ${(props) => (props.$isOpen ? "block" : "none")};
  }
`;

const DropdownItem = styled(Link)`
  display: block;
  padding: 12px 20px;
  color: #666;
  text-decoration: none;
  transition: all 0.3s;

  &:hover {
    background: #f8f9fa;
    color: #217fc4;
  }

  @media (max-width: 768px) {
    padding: 10px 0;
    font-size: 14px;
  }
`;

const MobileMenuButton = styled.button<{ $isScrolled?: boolean }>`
  display: none;
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  padding: 10px;
  color: ${(props) => (props.$isScrolled ? "#fff" : "#333")};
  transition: color 0.3s;

  @media (max-width: 768px) {
    display: block;
  }
`;

const MobileLanguageSwitch = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    gap: 10px;
    padding: 15px 20px;
    border-bottom: 2px solid #e9ecef;
    margin-bottom: 10px;

    button {
      flex: 1;
      padding: 8px 12px;
      border: 1px solid #217fc4;
      background: white;
      color: #217fc4;
      border-radius: 4px;
      cursor: pointer;
      transition: all 0.3s;
      font-size: 14px;
      font-weight: 500;

      &.active {
        background: #217fc4;
        color: white;
      }

      &:hover:not(.active) {
        background: #f0f8ff;
      }
    }
  }
`;

const Overlay = styled.div<{ $isOpen: boolean }>`
  display: none;

  @media (max-width: 768px) {
    display: ${(props) => (props.$isOpen ? "block" : "none")};
    position: fixed;
    top: 80px;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 999;
  }
`;

// 메뉴 데이터 타입 정의
interface MenuItem {
  titleKey: string;
  path: string;
  children?: MenuItem[];
}

// i18n 키를 사용하는 메뉴 데이터
const menuData: MenuItem[] = [
  {
    titleKey: "header.introduce",
    path: "/introduce",
    children: [
      { titleKey: "header.greeting", path: "/introduce/greeting" },
      { titleKey: "header.projects", path: "/introduce/performance" },
      { titleKey: "header.history", path: "/introduce/history" },
      { titleKey: "header.location", path: "/introduce/location" },
    ],
  },
  {
    titleKey: "header.business",
    path: "/business",
    children: [
      { titleKey: "header.engineering", path: "/business/engineering" },
      { titleKey: "header.products", path: "/business/equipment" },
      { titleKey: "header.software", path: "/business/software" },
    ],
  },
  {
    titleKey: "header.cs",
    path: "/cs",
    children: [
      { titleKey: "header.news", path: "/cs/news" },
      { titleKey: "header.faq", path: "/cs/faq" },
      { titleKey: "header.download", path: "/cs/data" },
    ],
  },
];

const Header: React.FC = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const { t } = useTranslation();
  const { language, changeLanguage } = useLanguage();

  // 스크롤 이벤트 핸들러
  useEffect(() => {
    const handleScroll = () => {
      // 헤더 높이(117px) 이상 스크롤되면 배경색 변경
      const scrolled = window.scrollY >= 117;
      setIsScrolled(scrolled);
    };

    // 스크롤 이벤트 리스너 등록
    window.addEventListener("scroll", handleScroll);

    // 초기 스크롤 상태 체크
    handleScroll();

    // 클린업 함수
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const handleDropdownToggle = (path: string) => {
    setOpenDropdown(openDropdown === path ? null : path);
  };

  const handleMobileMenuToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
    if (!mobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
  };

  const isActiveLink = (path: string) => {
    return location.pathname.startsWith(path);
  };

  // 컴포넌트 언마운트 시 body overflow 복원
  React.useEffect(() => {
    return () => {
      document.body.style.overflow = "unset";
    };
  }, []);

  return (
    <>
      <HeaderContainer $isScrolled={isScrolled}>
        <HeaderTop>
          <TopInner>
            <LanguageSwitch>
              <button
                className={language === "ko" ? "active" : ""}
                onClick={() => changeLanguage("ko")}
              >
                {t("language.ko")}
              </button>
              {/* <button
                className={language === "en" ? "active" : ""}
                onClick={() => changeLanguage("en")}
              >
                {t("language.en")}
              </button> */}
            </LanguageSwitch>
          </TopInner>
        </HeaderTop>

        <HeaderMain>
          <MainInner>
            <Logo to="/" $isScrolled={isScrolled}>
              <img
                src={`${process.env.PUBLIC_URL}/images/1main/logo.png`}
                alt="Company Logo"
              />
            </Logo>

            <Nav $isOpen={mobileMenuOpen}>
              {/* 모바일에서만 보이는 언어 선택 버튼 */}
              <MobileLanguageSwitch>
                <button
                  className={language === "ko" ? "active" : ""}
                  onClick={() => changeLanguage("ko")}
                >
                  {t("language.ko")}
                </button>
                {/* <button
                  className={language === "en" ? "active" : ""}
                  onClick={() => changeLanguage("en")}
                >
                  {t("language.en")}
                </button> */}
              </MobileLanguageSwitch>

              <NavList>
                {menuData.map((menu) => (
                  <NavItem key={menu.path}>
                    {menu.children ? (
                      <>
                        <NavLink
                          to={menu.path}
                          onClick={(e) => {
                            e.preventDefault();
                            // 모바일에서만 클릭 토글 동작
                            if (window.innerWidth <= 768) {
                              handleDropdownToggle(menu.path);
                            }
                          }}
                          $isActive={isActiveLink(menu.path)}
                          $isScrolled={isScrolled}
                        >
                          {t(menu.titleKey)}
                        </NavLink>
                        <Dropdown
                          $isOpen={
                            window.innerWidth <= 768
                              ? openDropdown === menu.path
                              : false
                          }
                        >
                          {menu.children.map((child) => (
                            <DropdownItem
                              key={child.path}
                              to={child.path}
                              onClick={() => {
                                setMobileMenuOpen(false);
                                setOpenDropdown(null);
                                document.body.style.overflow = "unset";
                              }}
                            >
                              {t(child.titleKey)}
                            </DropdownItem>
                          ))}
                        </Dropdown>
                      </>
                    ) : (
                      <NavLink
                        to={menu.path}
                        $isActive={isActiveLink(menu.path)}
                        $isScrolled={isScrolled}
                        onClick={() => {
                          setMobileMenuOpen(false);
                          document.body.style.overflow = "unset";
                        }}
                      >
                        {t(menu.titleKey)}
                      </NavLink>
                    )}
                  </NavItem>
                ))}
              </NavList>
            </Nav>

            <MobileMenuButton
              onClick={handleMobileMenuToggle}
              $isScrolled={isScrolled}
            >
              {mobileMenuOpen ? <FiX /> : <FiMenu />}
            </MobileMenuButton>
          </MainInner>
        </HeaderMain>
      </HeaderContainer>

      <Overlay
        $isOpen={mobileMenuOpen}
        onClick={() => {
          setMobileMenuOpen(false);
          document.body.style.overflow = "unset";
        }}
      />
    </>
  );
};

export default Header;
